import decorateComponentWithProps from "decorate-component-with-props";
import { Map } from "immutable";
import Mention from "./Mention";
import MentionSuggestions from "./MentionSuggestions"; // eslint-disable-line import/no-named-as-default
import MentionSuggestionsPortal from "./MentionSuggestionsPortal";
import defaultRegExp from "./defaultRegExp";
import hashtagStrategy from "./hashtagStrategy";
import mentionSuggestionsStrategy from "./mentionSuggestionsStrategy";
import suggestionsFilter from "./utils/defaultSuggestionsFilter";
import defaultPositionSuggestions from "./utils/positionSuggestions";
import { getDefaultKeyBinding } from "draft-js";
import { KEYBOARD_KEYS } from "constants/keyboard";
export { default as MentionSuggestions } from "./MentionSuggestions";

export * from "./styles";

export default (config = {}) => {
    // const defaultTheme = {
    //     // CSS class for mention text
    //     // mention: mentionStyles.mention,
    //     // // CSS class for suggestions component
    //     // mentionSuggestions: mentionSuggestionsStyles.mentionSuggestions,
    //     // // CSS classes for an entry in the suggestions component
    //     // mentionSuggestionsEntry: mentionSuggestionsEntryStyles.mentionSuggestionsEntry,
    //     // mentionSuggestionsEntryFocused: mentionSuggestionsEntryStyles.mentionSuggestionsEntryFocused,
    //     // mentionSuggestionsEntryText: mentionSuggestionsEntryStyles.mentionSuggestionsEntryText,
    //     // mentionSuggestionsEntryAvatar: mentionSuggestionsEntryStyles.mentionSuggestionsEntryAvatar,
    // };

    const defaultTheme = {
        // CSS class for mention text
        mention: "mention",
        // CSS class for suggestions component
        mentionSuggestions: "mentionSuggestions",
        // CSS classes for an entry in the suggestions component
        mentionSuggestionsEntry: "mentionSuggestionsEntry",
        mentionSuggestionsEntryFocused: "mentionSuggestionsEntryFocused",
        mentionSuggestionsEntryText: "mentionSuggestionsEntryText",
    };

    const callbacks = {
        keyBindingFn: undefined,
        handleKeyCommand: undefined,
        onDownArrow: undefined,
        onUpArrow: undefined,
        onTab: undefined,
        onEscape: undefined,
        handleReturn: undefined,
        onChange: undefined,
    };

    const ariaProps = {
        ariaHasPopup: "false",
        ariaExpanded: "false",
        ariaOwneeID: undefined,
        ariaActiveDescendantID: undefined,
    };

    let searches = Map();
    let escapedSearch;
    let clientRectFunctions = Map();
    let isOpened;

    const store = {
        getEditorState: undefined,
        setEditorState: undefined,
        getPortalClientRect: (offsetKey) => clientRectFunctions.get(offsetKey)?.(),
        getAllSearches: () => searches,
        isEscaped: (offsetKey) => escapedSearch === offsetKey,
        escapeSearch: (offsetKey) => {
            escapedSearch = offsetKey;
        },

        resetEscapedSearch: () => {
            escapedSearch = undefined;
        },

        register: (offsetKey) => {
            searches = searches.set(offsetKey, offsetKey);
        },

        updatePortalClientRect: (offsetKey, func) => {
            clientRectFunctions = clientRectFunctions.set(offsetKey, func);
        },

        unregister: (offsetKey) => {
            searches = searches.delete(offsetKey);
            clientRectFunctions = clientRectFunctions.delete(offsetKey);
        },

        getIsOpened: () => isOpened,
        setIsOpened: (nextIsOpened) => {
            isOpened = nextIsOpened;
        },
    };

    // Styles are overwritten instead of merged as merging causes a lot of confusion.
    //
    // Why? Because when merging a developer needs to know all of the underlying
    // styles which needs a deep dive into the code. Merging also makes it prone to
    // errors when upgrading as basically every styling change would become a major
    // breaking change. 1px of an increased padding can break a whole layout.
    const {
        mentionPrefix = "",
        theme = defaultTheme,
        positionSuggestions = defaultPositionSuggestions,
        mentionComponent,
        mentionSuggestionsComponent = MentionSuggestions,
        entityMutability = "SEGMENTED",
        mentionTrigger = "#",
        mentionRegExp = defaultRegExp,
    } = config;
    const mentionSearchProps = {
        ariaProps,
        callbacks,
        theme,
        store,
        entityMutability,
        positionSuggestions,
        mentionTrigger,
        mentionPrefix,
    };
    const keyToFunctionMapping = {
        [KEYBOARD_KEYS.DOWN]: "onDownArrow",
        [KEYBOARD_KEYS.TAB]: "onTab",
        [KEYBOARD_KEYS.UP]: "onUpArrow",
        [KEYBOARD_KEYS.ESCAPE]: "onEscape",
        [KEYBOARD_KEYS.ENTER]: "handleReturn",
    };
    return {
        MentionSuggestions: decorateComponentWithProps(mentionSuggestionsComponent, mentionSearchProps),
        decorators: [
            {
                strategy: hashtagStrategy,
                component: decorateComponentWithProps(Mention, { theme, mentionComponent }),
            },
            {
                strategy: mentionSuggestionsStrategy(mentionTrigger, mentionRegExp),
                component: decorateComponentWithProps(MentionSuggestionsPortal, { store }),
            },
        ],
        getAccessibilityProps: () => ({
            role: "combobox",
            ariaAutoComplete: "list",
            ariaHasPopup: ariaProps.ariaHasPopup,
            ariaExpanded: ariaProps.ariaExpanded,
            ariaActiveDescendantID: ariaProps.ariaActiveDescendantID,
            ariaOwneeID: ariaProps.ariaOwneeID,
        }),

        initialize: ({ getEditorState, setEditorState }) => {
            store.getEditorState = getEditorState;
            store.setEditorState = setEditorState;
        },
        keyBindingFn: (event) => {
            const fn = keyToFunctionMapping[event.key];
            if (fn) {
                callbacks[fn]?.(event);
                return null;
            }
            return getDefaultKeyBinding(event);
        },
        onChange: (editorState) => {
            if (callbacks.onChange) {
                return callbacks.onChange(editorState);
            }
            return editorState;
        },
    };
};

export const defaultSuggestionsFilter = suggestionsFilter;
